/* Purpose: Central page for viewing and editing all general information and files.
 * Props: ClientID (str)
 *        credentials (map)
 * Author: R. Markowitz (April 2025)
 * Dev Notes: 
*/

import React, {useState, useEffect} from 'react';
import {Alert, Container, Nav} from 'react-bootstrap';
import PageTitle from '../../Components/PageTitle';
import { validateField } from '../../Data/Regex';
import { personalInfo, financialInfo } from '../../Data/RequiredClientData';
import Personal from './Personal';
import Financial from './Financial'
import PortalNavBar from '../../Layout/PortalNavBar';
import { MAX_FILE_SIZE } from '../../Data/MaxData';

const General = (props) => {
    const ClientID = props.ClientID;
    const credentials = props.credentials;
    const [tab, setTab] = useState("Personal");
    const [loadingData, setLoadingData] = useState(true);
    const [saveBtnText, setSaveBtnText] = useState("Save");
    const [clickedSave, setClickedSave] = useState (true);
    const [stopSave, setStopSave] = useState(false);
    const [readyForDB, setReadyForDB] = useState(false);
    const [isSavingFile, setIsSavingFile] = useState(false); // Is true while file is being saved to database
    const [isUploadingFile, setIsUploadingFile] = useState(false); // Is true throughout the entire upload process (to set loading spinner)
    const [dbPersonalInfo, setDBPersonalInfo] = useState();
    const [dbFinancialInfo, setDBFinancialInfo] = useState();
    const [requPersonalInfo, setRequPersonalInfo] = useState(personalInfo.map((field) => ({
        ...field, value: ""
    })));
    const [requFinancialInfo, setRequFinancialInfo] = useState(financialInfo.map((field) => ({
        ...field, value: ""
    })))
    const [previewFileCache, setPreviewFileCache] = useState([]);
    

    useEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: 'smooth' });
    }, [])


    // When the page is loaded and ClientId and credentials are set, 
    // Fetch General Info from database and set the module variables
    useEffect(() => {
        const getGeneralInfo = async () => {
            setLoadingData(true);
            try {
                const response = await fetch(`https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/getGeneralInfo`, {
                    "method": "POST",
                    "body": JSON.stringify({
                        "ClientID": ClientID,
                        "credentials": credentials
                    }),
                });
                if (!response.ok) {
                    console.log("Error loading General info. Response not ok.");
                    alert("Something went wrong when trying to load your general information. Please Try refreshing the page. If the error persists, please contact info@iskamortgage.com")
                }else{
                    const data = await response.json();
                    if (data && !data == []) {
                            setDBPersonalInfo({...data.Personal});
                            setDBFinancialInfo({...data.Financial})

                            // Set Personal and Financial data
                            let infoPersonal = [...requPersonalInfo];
                            let infoFinancial = [...requFinancialInfo];

                            infoPersonal = infoPersonal.map((field) => ({
                            ...field,
                            value: data.Personal[field.fieldName]? data.Personal[field.fieldName]: "",
                            }))

                            infoFinancial = infoFinancial.map((field) => ({
                                ...field,
                                value: data.Financial[field.fieldName]? data.Financial[field.fieldName]: "",
                                }))
                    
                            setRequPersonalInfo([...infoPersonal]);
                            setRequFinancialInfo([...infoFinancial]);

                } else {
                    console.log("Data empty.");
                    alert("Something went wrong when trying to load your general information. Please Try refreshing the page. If the error persists, please contact info@iskamortgage.com")
                }
                }
            } catch (error) {
                console.log("Something went wrong when trying to load the clients general data. Error: ", error)
                alert("Something went wrong when trying to load your general information. Please Try refreshing the page. If the error persists, please contact info@iskamortgage.com")
            }
        }

        if (ClientID && credentials){
            getGeneralInfo();  
        }
        
    }, [ClientID, credentials]);

    //update loadingData when all the data is loaded and the variables are set
    useEffect(() => {
        setLoadingData(false);
    }, [dbFinancialInfo]);

    // When the user clicks out of a text box, the text will be validated
    const onBlur = ((item, index) => {
        let isValid = true;
        if (item.value !== ""){
            isValid = validateField(item)
        }     

        let tempInfo;
        if (tab =="Personal"){
            tempInfo = [...requPersonalInfo]
        }else{
            tempInfo = [...requFinancialInfo]
        }
        
        tempInfo[index] = {...tempInfo[index],
                        "isValid":isValid,
                        "value": item.value.trim()
                    };

        if(tab == "Personal"){
            setRequPersonalInfo(tempInfo);
        }else{
            setRequFinancialInfo(tempInfo);
        }
    });

    // When text is entered into any of the form control boxes, save the inputed value to requInfo[fieldName][value]
    // This will also make the inputed value appear in the form contol
    const handleInfoChange = async (index, event) => {

        let requInfo;
        if (tab == "Personal"){
            requInfo = requPersonalInfo
        }else{
            requInfo = requFinancialInfo
        }
    
        const updatedRequInfo = [...requInfo];
            
        // Update the specific item at the given index
        // If the updated value is a file
        if (updatedRequInfo[index].fieldType === "file"){
            const uploadedFile = event.target.files[0];
            if (uploadedFile.size <= MAX_FILE_SIZE){
                setIsUploadingFile(true);
                const filePath = ClientID + "/General_" + updatedRequInfo[index].fieldName + "_" + uploadedFile.name;
                event.preventDefault();

                const handleUploadToS3 = async (file, urlData) => {
                    const formData = new FormData();
                    Object.keys(urlData.fields).forEach(key => {
                    formData.append(key, urlData.fields[key]);
                    });
                    
                    formData.append('file', file);
                
                    console.log("fetching urlData.url...")
                    const response = await fetch(urlData.url, {
                    method: 'POST',
                    body: formData,
                    });
                
                    return response.ok;
                }
            
                try {
                    // Get the pre-signed URL from the Lambda function
                    const presignedResponse = await fetch('https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/saveFileToS3', {
                        "method": "POST",
                        "body": JSON.stringify({
                            "filePath": filePath,
                            "credentials": credentials
                        })
                    });
                    if (presignedResponse.ok){
                        const presignedData = await presignedResponse.json();
                
                        const isUploaded = await handleUploadToS3(uploadedFile, presignedData);
                        
                        

                        if (isUploaded) {
                            console.log("File uploaded successfully to S3!")
                            updatedRequInfo[index]= {
                                ...updatedRequInfo[index],
                                value: filePath, 
                                file: uploadedFile
                            };
                
                            // Save the uploaded file path in dynamodb
                            const updateFieldName = updatedRequInfo[index].fieldName
                            setDBPersonalInfo({...dbPersonalInfo, 
                            [updateFieldName] : filePath
                                })
                            setIsSavingFile(true);

                            // Put the file in the preview file cache
                            const url = URL.createObjectURL(uploadedFile);
                            setPreviewFileCache([...previewFileCache, {url, name:filePath}])
                        } else {
                            alert('Failed to upload file to S3.');
                            setIsUploadingFile(false);
                        }
                    }else{
                        const error = await presignedResponse.json()
                        alert(error)
                        console.log(("Error uploading File.", error))
                        setIsUploadingFile(false);

                    }  
                }catch (error){
                    console.error(error)
                    alert("Something went while uploading the file, ", error);
                    setIsUploadingFile(false);
                    
                }
            }else{
                alert("File too large. Try uploading a file less than 10 MB.")
            }

        // If the updated value is a regular text form field
        }else{
            updatedRequInfo[index]= {
                ...updatedRequInfo[index],
                value: event.target.value,
            };
        }



        if(tab == "Personal"){
            setRequPersonalInfo(updatedRequInfo);
            
        }else{
            setRequFinancialInfo(updatedRequInfo);
        }   
        
        setClickedSave(false)
    };


    // When Save is clicked, set the button text to saving..., 
    // save all the inputed info in the proper format into proper dbinfo,
    // set readyForDB to true to trigger updating the info in db
    const handleClickSave = async () => {
        setSaveBtnText("Saving...");

        let requInfo;
        if (tab == "Personal"){
            requInfo = requPersonalInfo
        }else{
            requInfo = requFinancialInfo
        }

        // Get all the values from financialRequ or personalRequ data lists and save them as info object with fieldName:value
        let tempList = {};
        requInfo.forEach((field, index) => {
        if ((field.fieldType === "text" && (field.value == "" || validateField(field))) ||field.fieldType !== "text"){
            requInfo[index] = {...requInfo[index], isValid: true}
            Object.assign(tempList, {[field.fieldName]: field.value})
        }else{
            requInfo[index] = {...requInfo[index], isValid: false}
        }
        });

        // If any of the fields are invalid, do not save.
        let hasInvalidFields = false
        requInfo.forEach((field, index) => {
        if (!field.isValid){
            hasInvalidFields = true;
        }
        })

        if (hasInvalidFields){
            setStopSave(true);
            setSaveBtnText("Save")
        }else{
            if (tab == "Personal"){
                setDBPersonalInfo({...tempList})
            }else{
                setDBFinancialInfo({...tempList})
            }

            setReadyForDB(true);
            setStopSave(false);
        }
    };

    // When readyForDB is true, update the info in dynamodb, when finished, set readyForDB back to false
    useEffect (() => {
        const saveToDB = async () => {
            if (readyForDB || isSavingFile){
                // Save the updated data to the database
                const params = {
                    method: "POST",
                    body: JSON.stringify({
                        "ClientID": ClientID,
                        "updateCategory": tab,
                        "updatedInfo": tab == "Personal"?dbPersonalInfo:dbFinancialInfo,
                        "credentials": credentials
                    })
                }

                const updateUrl = "https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/updateGeneralInfo";

                try {
                    const response = await fetch(updateUrl, params);

                    if (response.ok) {
                        alert("Your " + tab + " info was successfully updated!");
                        setClickedSave(true);
                        setSaveBtnText("Save");

                    } else {
                        let errorMsg = response.statusText
                        if (response.status == 400){
                            errorMsg = await response.json();
                        }

                        setSaveBtnText("Save");
                        alert('Something went wrong while trying to save your ' + tab + ' data. ' + errorMsg);
                        console.error('Error:', errorMsg);
                    }
                } catch (error) {
                    setSaveBtnText("Save");
                    alert("Something went wrong while trying to save your " + tab + " data.");
                    console.error('Error:', error);
                }
            }
        }

        saveToDB()
        setReadyForDB(false);
        setIsSavingFile(false);
        setIsUploadingFile(false)
    }, [readyForDB, isSavingFile]);

    // When back or next is pressed and the tab needs to changes
    const handleChangeTab = (destination) => {

        if(!clickedSave){
            alert("Your information has not been saved! Please save before leaving this page.")
        }else{
            setTab(destination)
        }
    }


    return(
        <>
            <PortalNavBar ClientID = {ClientID}/>

            <Container>
                <PageTitle pageName = "General Information" />
                <p className='text-center mb-4'>
                    <strong>Fill out the form and upload the necessary files. Make sure to SAVE before leaving this page. All fields can be changed after saving.</strong>
                    <br />
                    **Only files with the following extensions can be uploaded: .pdf, .jpg, .png**
                </p>

                {loadingData && (
                    <Container className = 'mx-3'>
                        <Alert height = '50%' variant = 'info'>
                            Loading Loan Data...<br />
                            Do not try to edit any fields while data is loading!
                        </Alert>
                    </Container>
                )}

                {!loadingData && (
                    <>
                        <Nav variant='tabs' activeKey={tab} onSelect={(selectedKey) => setTab(selectedKey)}>
                            <Nav.Item>
                                <Nav.Link eventKey = "Personal">Personal</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey = "Financial">Financial</Nav.Link>
                            </Nav.Item>
                        </Nav>
                    


                        {tab == "Personal" && (
                        <Personal
                            id = "Personal"
                            onBlur = {onBlur}
                            handleChangeTab = {handleChangeTab}
                            credentials = {credentials}
                            clickedSave = {clickedSave}
                            handleClickSave={handleClickSave}
                            stopSave = {stopSave}
                            setStopSave = {setStopSave}
                            saveBtnText={saveBtnText}
                            setRequPersonalInfo = {setRequPersonalInfo}
                            requPersonalInfo = {requPersonalInfo}
                            previewFileCache = {previewFileCache}
                            setPreviewFileCache = {setPreviewFileCache}
                            dbPersonalInfo = {dbPersonalInfo}
                            setDBPersonalInfo = {setDBPersonalInfo}
                            handleInfoChange = {handleInfoChange}
                            isUploadingFile={isUploadingFile}
                            setIsSavingFile={setIsSavingFile}
                        />
                        )}

                        {tab == "Financial" && (
                        <Financial
                            id = "Financial"
                            onBlur = {onBlur}
                            handleChangeTab = {handleChangeTab}
                            saveBtnText={saveBtnText}
                            clickedSave = {clickedSave}
                            stopSave = {stopSave}
                            requFinancialInfo = {requFinancialInfo}
                            handleClickSave={handleClickSave}
                            handleInfoChange = {handleInfoChange}
                        />
                        )}
                    </>
                )}

            </Container>
        </>
    );
};

export default General